/* eslint-disable max-nested-callbacks */
import { defineStore } from 'pinia'

import { ENOVIA_GROUP_STATUS_NAME } from '~/config/tagger-or-enovia'

import { toMaturity } from '~/entities/content/question/api/exalead-questions'

import SwymServices from '~/features-shared/content/api/contents'
import ExaleadServices from '~/features-shared/content/api/exalead-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { useRefineGlobalContentsStore } from '~/features-shared/content-feed/composables/refine-contents'
import { ServiceFetchControllerDefault } from '~/features-shared/content-feed/controllers/services-fetch-controller-default'
import type { QuestionDS6WMaturity } from '~/features-shared/maturities/interfaces/exalead-maturities'
import { QuestionMaturity } from '~/features-shared/maturities/interfaces/maturities'

const QUESTION_EXALEAD_STATUS_GROUP_NAME = ENOVIA_GROUP_STATUS_NAME

export const useRefineQuestionsStore = defineStore('refineQuestions', () => {
  const store = useRefineGlobalContentsStore(
    new ServiceFetchControllerDefault(SwymServices[IContentModel.Question], ExaleadServices[IContentModel.Question]),
  )

  const maturities = computed(() => {
    const groups = store.getGroups(store.context.value)

    const status = groups && groups.find(g => g.id === QUESTION_EXALEAD_STATUS_GROUP_NAME)
    const category = status && status.categories && status.categories.length && status.categories[0]
    const subCategories = category && category.categories

    const defaultMaturities = {
      [QuestionMaturity.Validated]: 0,
      [QuestionMaturity.Answered]: 0,
      [QuestionMaturity.NotAnswered]: 0,
    }

    const extractedMaturities = subCategories && subCategories
      .map(c => ({ id: toMaturity(c.title as QuestionDS6WMaturity), count: c.count || 0 }))
      .reduce((pre, cur) => ({ ...pre, ...{ [cur.id]: cur.count } }), {})

    const maturities = {
      ...defaultMaturities,
      ...extractedMaturities,
    }

    return maturities
  })

  const computations = {
    validated: computed(() => maturities.value[QuestionMaturity.Validated]),
    answered: computed(() => maturities.value[QuestionMaturity.Answered]),
    notanswered: computed(() => maturities.value[QuestionMaturity.NotAnswered]),
  }

  return {
    // States
    limit: store.limit,
    groups: store.groups,

    // Tags
    tags: store.tags,
    tagsWithSubjectURIData: store.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: store.tagsWithoutSubjectURIData,

    // Computed
    total: store.total,
    currentContents: store.currentContents,

    validated: computations.validated,
    answered: computations.answered,
    notanswered: computations.notanswered,

    // Getters
    getContents: store.getContents,

    // Actions
    doFetchNext: store.doFetchNext,
    doFetchMeta: store.doFetchMeta,
    doInsert: store.doInsert,
    doClear: store.doClear,
    doClearGroups: store.doClearGroups,
  }
})
